/**
 * Declarative registry of object-spec fields shared by INFORMACE, phase-confirm review,
 * and milestone validation. Czech labels are the UI SSOT — keep them Czech.
 *
 * Mobile clients consume the exported JSON (`export-spec-field-registry.js`).
 * Storage policy: new fields default to a typed column on `pripojovany_objekt_spec`
 * (or versioned JSON column) — do **not** add new `metadata.customFields` keys for spec data.
 */

export const SPEC_FIELD_IDS = [
  'HP_TOTAL',
  'HP_NEBYT',
  'FLOORS',
  'HORIZONTALNI_PROPOJE',
  'UKONCENI_V_RD',
  'BABOVKA_PLACEMENT',
  'NAPOJENE_MT',
  'WELD_COUNT',
  'WELD_COUNT_TOTAL',
  'WELD_COUNT_PRIMARY',
  'WELD_COUNT_SECONDARY',
  'ROZVADEC_TYP',
  'ZAFUK',
  'VR_REPORT',
  'PRIVODNI_MT',
  'DIGITALNI_CESTA',
  'DELKA_PRIPOJKY',
] as const;

export type SpecFieldId = (typeof SPEC_FIELD_IDS)[number];

export type SpecFieldVisibility = {
  /** INFORMACE / object detail spec rows */
  info: boolean;
  /** Phase-confirm / milestone-confirm review sheet */
  phaseConfirm: boolean;
  /** Milestone completion validation (spec sheet gate) */
  milestoneValidation: boolean;
};

export type SpecFieldDef = {
  id: SpecFieldId;
  /** Czech UI label (INFORMACE row / review block title). */
  labelCs: string;
  /** Hint for where the value lives (typed column or known metadata path). */
  storagePath: string;
  visibility: SpecFieldVisibility;
};

export const SPEC_FIELD_REGISTRY: readonly SpecFieldDef[] = [
  {
    id: 'HP_TOTAL',
    labelCs: 'HP celkem',
    storagePath: 'spec.hpCountTotal',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'HP_NEBYT',
    labelCs: 'Z toho nebytových HP',
    storagePath: 'spec.hpNebytCount',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'FLOORS',
    labelCs: 'Počet pater',
    storagePath: 'spec.floors',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'HORIZONTALNI_PROPOJE',
    labelCs: 'Propoj',
    storagePath: 'spec.horizontalniPropoje',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'UKONCENI_V_RD',
    labelCs: 'Ukončení v RD',
    storagePath: 'spec.ukonceniVRd',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'BABOVKA_PLACEMENT',
    labelCs: 'Umístění bábovky',
    storagePath: 'spec.babovkaPlacement',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'NAPOJENE_MT',
    labelCs: 'Napojené MT',
    storagePath: 'spec.napojeneMtBarvy | metadata.napojeneMtBarvy',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'WELD_COUNT',
    labelCs: 'Počet svarů',
    storagePath: 'object.weldCountTotal | commit.content.weldCountSvaru',
    visibility: { info: false, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'WELD_COUNT_TOTAL',
    labelCs: 'Počet svarů',
    storagePath: 'object.weldCountTotal',
    visibility: { info: true, phaseConfirm: false, milestoneValidation: false },
  },
  {
    id: 'WELD_COUNT_PRIMARY',
    labelCs: 'Počet svarů — kazety / osazení',
    storagePath: 'object.weldCountPrimary',
    visibility: { info: false, phaseConfirm: false, milestoneValidation: false },
  },
  {
    id: 'WELD_COUNT_SECONDARY',
    labelCs: 'Počet svarů — přímé / budovy',
    storagePath: 'object.weldCountSecondary',
    visibility: { info: false, phaseConfirm: false, milestoneValidation: false },
  },
  {
    id: 'ROZVADEC_TYP',
    labelCs: 'Typ rozvaděče',
    storagePath: 'rozvadecSpec.typeId',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: false },
  },
  {
    id: 'ZAFUK',
    labelCs: 'Záfuk',
    storagePath: 'spec.zafuk* | metadata.zafuk*',
    visibility: { info: false, phaseConfirm: true, milestoneValidation: true },
  },
  {
    id: 'VR_REPORT',
    labelCs: 'VR zpráva',
    storagePath: 'metadata.customFields.pripojovanyVrTechnicalReport',
    visibility: { info: false, phaseConfirm: true, milestoneValidation: false },
  },
  {
    id: 'PRIVODNI_MT',
    labelCs: 'Přívodní MT',
    storagePath: 'spec.privodniMtLengthM',
    visibility: { info: true, phaseConfirm: false, milestoneValidation: false },
  },
  {
    id: 'DIGITALNI_CESTA',
    labelCs: 'Digitální cesty',
    storagePath: 'spec.digitalniCesta',
    visibility: { info: true, phaseConfirm: true, milestoneValidation: false },
  },
  {
    id: 'DELKA_PRIPOJKY',
    labelCs: 'Délka přípojky',
    storagePath: 'branch-status.branchPripojkaLengthM (sum surfaces on branch VYKOP)',
    visibility: { info: true, phaseConfirm: false, milestoneValidation: false },
  },
] as const;

const BY_ID: Record<SpecFieldId, SpecFieldDef> = SPEC_FIELD_REGISTRY.reduce(
  (acc, def) => {
    acc[def.id] = def;
    return acc;
  },
  {} as Record<SpecFieldId, SpecFieldDef>,
);

export function getSpecFieldDef(id: SpecFieldId | string): SpecFieldDef | undefined {
  return BY_ID[id as SpecFieldId];
}

export function specFieldsForInfo(): readonly SpecFieldDef[] {
  return SPEC_FIELD_REGISTRY.filter((f) => f.visibility.info);
}

export function specFieldsForPhaseConfirm(): readonly SpecFieldDef[] {
  return SPEC_FIELD_REGISTRY.filter((f) => f.visibility.phaseConfirm);
}

export function specFieldLabelCs(id: SpecFieldId | string): string {
  return getSpecFieldDef(id)?.labelCs ?? String(id);
}

/**
 * Milestone / target-phase → which SpecFieldIds appear in phase-confirm review.
 * Empty array = no spec section for that step. `null` caller context = no filter.
 */
export const PHASE_CONFIRM_FIELDS_BY_MILESTONE: Record<string, readonly SpecFieldId[]> = {
  ZAVRTANY: ['UKONCENI_V_RD'],
  ZRIZENE_VR: ['HP_TOTAL', 'HP_NEBYT', 'FLOORS', 'HORIZONTALNI_PROPOJE'],
  POSTAVENA_BABOVKA: ['BABOVKA_PLACEMENT'],
  // Záfuk form: délka/rozvaděč/metráž; typ OK se odvodí z materiálu (+ Napojené MT ze spojkoviště).
  ZAFOUKNUTO: ['ZAFUK', 'NAPOJENE_MT'],
  ODBOCENO: ['NAPOJENE_MT'],
  PROVARENI: ['ROZVADEC_TYP', 'WELD_COUNT'],
  PROVARENI_PRIMARNIHO_OK: ['ROZVADEC_TYP', 'WELD_COUNT'],
  PROVARENI_SEKUNDARNICH_OK: ['ROZVADEC_TYP', 'WELD_COUNT'],
};

/** Phase-name heuristics for linear phase confirm (code may be WF.PHASE.N or named). */
export const PHASE_CONFIRM_FIELDS_BY_PHASE_HINT: ReadonlyArray<{
  match: RegExp;
  fields: readonly SpecFieldId[];
}> = [
  {
    match: /ZRIZENE_VR|BUDOVA_ZRIZENE_VR/i,
    fields: ['HP_TOTAL', 'HP_NEBYT', 'FLOORS', 'HORIZONTALNI_PROPOJE'],
  },
  {
    match: /POSTAVENA_BABOVKA|BUDOVA_POSTAVENA_BABOVKA/i,
    fields: ['BABOVKA_PLACEMENT'],
  },
  { match: /ROZVADEC\.1\.PHASE\.1\b/i, fields: ['ROZVADEC_TYP'] },
  // Lineární Zafouknutý = BUDOVA.2|3.PHASE.2 (kód neobsahuje „ZAFOUKNUTO").
  {
    match: /BUDOVA\.[23]\.PHASE\.2\b/i,
    fields: ['ZAFUK', 'NAPOJENE_MT'],
  },
  { match: /ROZVADEC\.1\.PHASE\.2\b/i, fields: ['ZAFUK'] },
  { match: /ROZVADEC\.1\.PHASE\.3\b/i, fields: ['ROZVADEC_TYP', 'WELD_COUNT'] },
  { match: /ROZVADEC_PROVARENE/i, fields: ['ROZVADEC_TYP', 'WELD_COUNT'] },
  { match: /ZAFOUKNUTO_SLOUPEK/i, fields: ['ZAFUK'] },
  { match: /ZAFOUKNUTO/i, fields: ['ZAFUK', 'NAPOJENE_MT'] },
  { match: /ZRIZENO.*ROZVADEC|ROZVADEC.*ZRIZENO/i, fields: ['WELD_COUNT'] },
  // SPOJKOVISTE Připojené: napojené MT + Bod záfuku (metráž / zdroj) when checkbox is on.
  { match: /PRIPOJENE|SPOJKOVISTE/i, fields: ['NAPOJENE_MT', 'ZAFUK'] },
];

export function phaseConfirmFieldsForMilestone(
  milestoneId: string | null | undefined,
): readonly SpecFieldId[] {
  if (!milestoneId) return [];
  return PHASE_CONFIRM_FIELDS_BY_MILESTONE[milestoneId.trim().toUpperCase()] ?? [];
}

export function phaseConfirmFieldsForTargetPhase(
  targetPhaseCode: string | null | undefined,
): readonly SpecFieldId[] {
  const code = (targetPhaseCode ?? '').trim();
  if (!code) return [];
  for (const entry of PHASE_CONFIRM_FIELDS_BY_PHASE_HINT) {
    if (entry.match.test(code)) return entry.fields;
  }
  return [];
}

/** JSON shape written by `export-spec-field-registry.js` for KMP. */
export type SpecFieldRegistryExport = {
  fields: readonly SpecFieldDef[];
  phaseConfirmByMilestone: Record<string, readonly SpecFieldId[]>;
  phaseConfirmByPhaseHint: ReadonlyArray<{ match: string; fields: readonly SpecFieldId[] }>;
};

export function buildSpecFieldRegistryExport(): SpecFieldRegistryExport {
  return {
    fields: SPEC_FIELD_REGISTRY,
    phaseConfirmByMilestone: PHASE_CONFIRM_FIELDS_BY_MILESTONE,
    phaseConfirmByPhaseHint: PHASE_CONFIRM_FIELDS_BY_PHASE_HINT.map((e) => ({
      match: e.match.source,
      fields: e.fields,
    })),
  };
}
